import Reveal from '../ui/Reveal.jsx';
import Eyebrow from '../ui/Eyebrow.jsx';
import { brand, contact } from '../../content/site.js';

const buildLinks = [
  { href: '#offers', label: 'The Foundry' },
  { href: '#offers', label: 'Automated Systems' },
  { href: '#offers', label: 'Discovery Session' },
  { href: '/case-studies', label: 'Case Studies' },
  { href: '#process', label: 'Process' },
  { href: '/adventure-logs', label: 'Adventure Logs' },
];

const trainLinks = [
  { href: '#offers', label: 'Enterprise AI Training' },
  { href: '#offers', label: 'AI Readiness Audit' },
  { href: '#how-we-work', label: 'How we work' },
  { href: '#faq', label: 'FAQ' },
  { href: '#cta', label: 'Talk to Merlin' },
];

/**
 * Footer — flat dark close in the hero's design language: hairline top
 * border, eyebrow-dash column labels, gradient-accent brand line, mono
 * contact rail, and dash-slide micro-motion on link hover. No washes,
 * no patterns — same surface as the hero.
 */
export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-surface text-ink-200 border-t border-white/[.06] pt-20 pb-8 text-sm overflow-hidden">
      <div className="container-page">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_repeat(3,1fr)] gap-10 mb-14">
          {/* Brand column */}
          <Reveal>
            <a href="#top" aria-label="The Artificer's AI home" className="inline-block">
              <img
                src="/logos/artificers-logo-white.png"
                alt="The Artificer's AI"
                className="h-[42px] w-auto block"
              />
            </a>
            <p className="mt-6 font-display font-semibold text-display-sm leading-snug tracking-[-0.02em] text-ink-50 max-w-[16ch]">
              Your AI Transformation Partners.{' '}
              <span className="text-gradient-accent">Built in Perth, working worldwide.</span>
            </p>
          </Reveal>

          <FooterColumn title="Pillar 01 · Build" links={buildLinks} delay={80} />
          <FooterColumn title="Pillar 02 · Train" links={trainLinks} delay={160} />

          <Reveal delay={240}>
            <FooterTitle>Contact</FooterTitle>
            <ul className="list-none p-0 m-0 flex flex-col gap-3 font-mono text-mono-md tracking-[0.05em]">
              <li>
                <FooterLink href={`mailto:${contact.email}`}>{contact.email}</FooterLink>
              </li>
              <li>
                <FooterLink href={contact.phoneHref}>{contact.phone}</FooterLink>
              </li>
              <li className="text-ink-300">Perth, Western Australia</li>
              <li>
                <FooterLink href={brand.url}>artificersai.com</FooterLink>
              </li>
            </ul>
          </Reveal>
        </div>

        {/* SMS programme disclosure — CTIA / A2P 10DLC.
            Server-rendered on purpose. When the LeadConnector chat widget is
            deployed it carries its own consent checkbox, but that widget is
            injected by JavaScript, so a validator reading raw HTML sees no
            opt-in disclosure anywhere on the site. This block puts the required
            language in the markup on every page: programme description,
            frequency, rates, STOP/HELP, consent-not-a-condition, and links to
            both policies.

            The wording is deliberately channel-neutral ("if you opt in to SMS"),
            not "through the chat", so it stays accurate whether or not the chat
            widget is deployed. Do not tie it back to a specific mechanism.

            It is disclosure, NOT a collection mechanism — never add an input,
            checkbox or form here, or it becomes a second opt-in path and fails
            the exclusivity rule. See docs/agent/a2p-verification.md. */}
        <Reveal
          delay={80}
          as="section"
          aria-label="SMS programme disclosure"
          className="pt-7 border-t border-white/[.06] text-ink-300 max-w-[80ch]"
        >
          <p className="text-xs leading-relaxed">
            <span className="text-ink-200 font-semibold">SMS.</span>{' '}
            If you opt in to SMS from us, Artificer's AI Solutions may send
            you SMS about your enquiry, appointments, services and account. Message
            frequency varies. Message and data rates may apply. Reply STOP to unsubscribe
            or HELP for help. Consent is not a condition of purchase. No mobile
            information is shared with third parties or affiliates for marketing purposes.
            See our{' '}
            <a href="/privacy-policy" className="text-ink-200 underline hover:text-white transition-colors">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/sms-terms" className="text-ink-200 underline hover:text-white transition-colors">
              SMS Terms
            </a>
            .
          </p>
        </Reveal>

        <Reveal
          delay={100}
          className="flex flex-wrap items-center justify-between gap-4 pt-7 mt-7 border-t border-white/[.06] font-mono text-mono-md tracking-[0.04em]"
        >
          <div>© {year} The Artificer's AI · Perth, WA</div>
          {/* A2P 10DLC registration requires both documents to be reachable
              from the site chrome. Do not remove or rename these links. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <FooterBarLink href="/privacy-policy">Privacy Policy</FooterBarLink>
            <span aria-hidden="true" className="text-ink-300">·</span>
            <FooterBarLink href="/sms-terms">SMS Terms</FooterBarLink>
          </div>
          <div>ABN-registered · Australian-owned</div>
        </Reveal>
      </div>
    </footer>
  );
}

function FooterTitle({ children }) {
  return <Eyebrow as="h4" className="mb-5">{children}</Eyebrow>;
}

/* Dash-slide hover: a hairline grows in from the left edge while the label
   shifts right — the eyebrow dash motif, animated. Transform-only. */
function FooterLink({ href, children }) {
  return (
    <a
      href={href}
      className="group relative inline-flex items-center text-ink-200 hover:text-white transition-colors duration-200"
    >
      <span
        aria-hidden="true"
        className="absolute left-0 top-1/2 h-px w-3.5 origin-left scale-x-0 bg-brand-blue-bright transition-transform duration-300 group-hover:scale-x-100"
      />
      <span className="transition-transform duration-300 ease-out group-hover:translate-x-[22px]">
        {children}
      </span>
    </a>
  );
}

/* Bottom-bar variant: no dash-slide. The hover translate on FooterLink is
   tuned for vertical stacks and would collide with neighbours in a row. */
function FooterBarLink({ href, children }) {
  return (
    <a
      href={href}
      className="text-ink-200 hover:text-white transition-colors duration-200"
    >
      {children}
    </a>
  );
}

function FooterColumn({ title, links, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <FooterTitle>{title}</FooterTitle>
      <ul className="list-none p-0 m-0 flex flex-col gap-3">
        {links.map((l) => (
          <li key={`${l.href}-${l.label}`}>
            <FooterLink href={l.href}>{l.label}</FooterLink>
          </li>
        ))}
      </ul>
    </Reveal>
  );
}
